import { useState } from 'react'
import {
  ContractAnalysisType,
  CreateTask,
  useGetContractsQuery,
  ErrorMessage,
  getSubstanceText,
  ModalDialog,
  Mtds,
  templates,
  useCreateTaskMutation,
  useGetPlanByIdQuery,
  useErrorToast,
  useGetBatchDataQuery,
  widgets,
  useAddContractAnalysisTypeMutation,
  CheckCircleIcon,
  MicroscopeIcon,
  Task
} from 'prove-common'
import { getTaskSchema, type TaskFormData } from '../../schemas/task-schema'
import { createTaskUiSchema } from '../../schemas/task-ui-schema'
import { useExistingOrCreateAnalysisType } from '../../hooks/useExistingOrCreateAnalysisType'
import {
  filterBatchData,
  findValidContractAnalysisTypes
} from '../../utils/taskUtils'
import { useNavigate } from 'react-router'
import { Form, RJSFSchema, validator } from 'prove-common/rjsf'
import { useTaskReceptionAddress } from '../../hooks/useTaskReceptionAddress'
import { TaskReceptionAddress } from '../task-reception-address/TaskReceptionAddress'
import { MatrixAmountFields } from '../matrix-amount-fields/MatrixAmountFields'
import {
  parseMatrixAmountDraft,
  toMatrixAmountDraft,
  type MatrixAmountDraft
} from '../../utils/matrixAmount'

type CopyTaskModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  task: Task
  destinationPlanId?: number
  debug?: boolean
}

type StepHeaderProps = {
  index: number
  title: string
  currentStep: number
  canActivate: boolean
  onToggleRequest: () => void
  alertLabel?: string
  isFinished: boolean
}

const StepHeader = ({
  index,
  title,
  currentStep,
  canActivate,
  onToggleRequest,
  alertLabel,
  isFinished
}: StepHeaderProps) => {
  return (
    <Mtds.Details.Summary
      className={canActivate ? undefined : 'opacity-60'}
      onClick={(event) => {
        event.preventDefault()
        if (!canActivate) return
        onToggleRequest()
      }}
    >
      <Mtds.Grid>
        <Mtds.Flex
          data-justify={'space-between'}
          data-align={'center'}
          data-size={'lg'}
        >
          <Mtds.Flex data-align={'center'}>
            {isFinished && <CheckCircleIcon />}
            {title}
          </Mtds.Flex>
          <div
            data-icon='none'
            data-size={'md'}
            className={`rounded-md px-2 py-1 ${index === currentStep ? 'bg-surface-tinted' : undefined}`}
          >
            {index} av 3
          </div>
        </Mtds.Flex>
        {alertLabel && (
          <Mtds.Alert data-color={'info'} data-size={'md'}>
            <Mtds.Heading>{alertLabel}</Mtds.Heading>
          </Mtds.Alert>
        )}
      </Mtds.Grid>
    </Mtds.Details.Summary>
  )
}

export default function CopyTaskModal(props: Readonly<CopyTaskModalProps>) {
  return props.isOpen ? <CopyTaskForm key={props.task.id} {...props} /> : null
}

function CopyTaskForm({
  isOpen,
  setOpen,
  task: latestTask,
  destinationPlanId,
  debug
}: Readonly<CopyTaskModalProps>) {
  const [task] = useState(latestTask)
  const targetPlanId = destinationPlanId ?? task.planIds?.[0]
  const destination = useGetPlanByIdQuery(targetPlanId)
  const navigate = useNavigate()
  const { data: contracts } = useGetContractsQuery(isOpen)
  const parentContractId = task.contractId
  const parentContract = parentContractId
    ? contracts?.find((contract) => contract.id === parentContractId)
    : undefined
  // steps: 0 Kontrakt, 1 Prøvemateriale, 2 Tilleggsinformasjon, 3 Antall prøver
  const [step, setStep] = useState<number>(parentContractId ? 1 : 0)
  const [formData, setFormData] = useState<Partial<TaskFormData> | undefined>(
    () => ({
      labId: task.lab?.id,
      labReceptionAddressId: task.labReceptionAddressId,
      contractId: task.contractId,

      productId: task.analysisType?.sampleType?.productId,
      matrixId: task.analysisType?.sampleType?.matrixId,
      substanceIds: task.analysisType?.substances?.map(
        (substance) => substance.substance.id
      ),
      payloadSchemaId: task.analysisType?.payloadSchema?.id,

      sampntTypeId: task.sampntType?.id,
      ordningsverdiId: task.ordningsVerdi?.id,
      description: task.description ?? '',
      locationRequired: task.locationRequired,

      countSubSamples: task.countSubSamples,
      quantity: task.quantity
    })
  )
  const [matrixAmountDraft, setMatrixAmountDraft] = useState<MatrixAmountDraft>(
    () => toMatrixAmountDraft(task.matrixAmount)
  )
  const { amount: matrixAmount, error: matrixAmountError } =
    parseMatrixAmountDraft(matrixAmountDraft)
  const hasMatrixAmount = matrixAmount != null && matrixAmountError == null
  const receptionAddress = useTaskReceptionAddress(
    formData?.labId,
    formData?.labReceptionAddressId,
    isOpen
  )
  const {
    data: batchData,
    isPending: isPendingBatchData,
    isError: isErrorBatchData,
    error: batchDataError
  } = useGetBatchDataQuery(isOpen)
  const createTask = useCreateTaskMutation()
  const showError = useErrorToast('kopiere oppgaven')
  const existingOrCreateAnalysisType = useExistingOrCreateAnalysisType(
    batchData?.sampleTypes,
    batchData?.analysisTypes
  )
  const addContractAnalysisType = useAddContractAnalysisTypeMutation()
  const canProceedFromStep0 = (): boolean => formData?.contractId != null
  const canProceedFromStep1 = (): boolean =>
    formData?.productId != null &&
    formData?.matrixId != null &&
    (formData?.substanceIds?.length ?? 0) > 0 &&
    formData?.payloadSchemaId != null
  const canProceedFromStep2 = (): boolean =>
    formData?.sampntTypeId != null && formData?.ordningsverdiId != null
  const canSubmit = (): boolean =>
    receptionAddress.canSave &&
    hasMatrixAmount &&
    formData?.quantity != null &&
    formData?.countSubSamples != null &&
    canProceedFromStep1() &&
    canProceedFromStep2()

  const handleStepToggle = (index: number) => {
    setStep((prev) => (prev === index ? -1 : index))
  }

  const validAnalysisTypes = findValidContractAnalysisTypes({
    formData,
    contracts
  })
  const matchingAnalysisTypes = validAnalysisTypes.flatMap(
    (contract) => contract.analysisTypes ?? []
  )
  const hasAnyCombination = matchingAnalysisTypes.length > 0
  const hasApprovedCombination = matchingAnalysisTypes.some(
    (analysisType) => analysisType.mtApproval && analysisType.labApproval
  )
  const showAwaitingApprovalWarning =
    canProceedFromStep1() && hasAnyCombination && !hasApprovedCombination
  const showNewCombinationWarning = canProceedFromStep1() && !hasAnyCombination
  let step1AlertLabel: string | undefined
  if (showAwaitingApprovalWarning) {
    step1AlertLabel = 'Avventer godkjenning'
  } else if (showNewCombinationWarning) {
    step1AlertLabel = 'Ny kombinasjon sendes til godkjenning'
  }
  const validContracts = formData?.labId
    ? contracts?.filter((contract) => contract?.labId === formData.labId)
    : contracts
  const { valid, invalid } = batchData
    ? filterBatchData(batchData, validAnalysisTypes)
    : { valid: null, invalid: null }
  const fullSchema: RJSFSchema | null =
    valid && invalid
      ? getTaskSchema(valid, invalid, validContracts ?? [])
      : null

  // Utility to build subset schemas for steps
  const buildSubsetSchema = (
    keys: string[]
  ): RJSFSchema | Record<string, never> => {
    if (!fullSchema) return {}
    const { properties = {}, required = [], ...rest } = fullSchema
    const subProps = Object.fromEntries(
      Object.entries(properties).filter(([k]) => keys.includes(k))
    )
    const subRequired = required.filter((r) => keys.includes(r))
    return { ...rest, properties: subProps, required: subRequired }
  }

  const step0Keys = ['labId', 'contractId']
  const step1Keys = ['productId', 'matrixId', 'substanceIds', 'payloadSchemaId']
  const step2Keys = [
    'sampntTypeId',
    'ordningsverdiId',
    'description',
    'locationRequired'
  ]
  const step3Keys = ['quantity', 'countSubSamples']

  const step0Schema = step === 0 ? buildSubsetSchema(step0Keys) : {}
  const step1Schema = step === 1 ? buildSubsetSchema(step1Keys) : {}
  const step2Schema = step === 2 ? buildSubsetSchema(step2Keys) : {}
  const step3Schema = step === 3 ? buildSubsetSchema(step3Keys) : {}

  const submitFormData = async (fd: TaskFormData): Promise<void> => {
    if (
      !receptionAddress.canSave ||
      !destination.data?.canCreateChildren ||
      destination.isFetching
    )
      return
    try {
      const analysisType = await existingOrCreateAnalysisType.mutateAsync({
        selectedProductId: fd.productId,
        selectedMatrixId: fd.matrixId,
        selectedSubstanceIds: fd.substanceIds,
        selectedPayloadSchemaId: fd.payloadSchemaId
      })

      // Add analysis type to contract if not already added
      const contract = contracts?.find((c) => c.id === fd.contractId)
      if (
        contract &&
        !contract.analysisTypes?.some(
          (a) => a.analysisType?.id === analysisType.id
        )
      ) {
        await addContractAnalysisType
          .mutateAsync({
            contractId: contract.id,
            body: { analysisTypeId: analysisType.id }
          })
          .then(() =>
            Mtds.toast.success('Analysetypen ble lagt til i kontrakten.')
          )
      }

      const taskNew: CreateTask = {
        planIds: [targetPlanId].filter((id): id is number => id != null),
        analysisTypeId: analysisType.id,
        quantity: fd.quantity,
        sampntTypeId: fd.sampntTypeId,
        labId: fd.labId,
        labReceptionAddressId: formData?.labReceptionAddressId,
        contractId: fd.contractId,
        description: fd.description,
        ownerId: 'self',
        countSubSamples: fd.countSubSamples,
        ordningsverdiId: fd.ordningsverdiId,
        planStatus: task.planStatus,
        locationRequired: fd.locationRequired,
        matrixAmount
      }
      const createdTask = await createTask.mutateAsync(taskNew)
      setOpen(false)
      Mtds.toast.success('Oppgaven er kopiert og lagt til i planen')
      const planId = createdTask.planIds?.[0] ?? task.planIds?.[0]
      if (!planId) {
        Mtds.toast.danger(
          'Kunne ikke finne en tilknyttet plan for den kopierte oppgaven.'
        )
        return
      }
      navigate(`/plans/${planId}/tasks/${createdTask.id}`)
    } catch (e) {
      showError(e)
    }
  }

  const handleSelectAnalysisType = (
    selectedAnalysisTypeId: number,
    selectedContractId: number
  ) => {
    const selectedContract = validAnalysisTypes.find(
      (v) => v.id === Number(selectedContractId)
    )
    const analysisType: ContractAnalysisType | undefined =
      selectedContract?.analysisTypes?.find(
        (a) => a.id === selectedAnalysisTypeId
      )
    const sampleTypeId = analysisType?.analysisType?.sampleType?.id
    const substanceIds = analysisType?.analysisType?.substances?.map(
      (s) => s.substance.id
    )
    const payloadSchemaId = analysisType?.analysisType?.payloadSchema?.id
    const labId = selectedContract?.labId
    setFormData((prev) => {
      const base = prev ?? ({} as TaskFormData)
      if (sampleTypeId && substanceIds && labId && payloadSchemaId) {
        return {
          ...base,
          sampleTypeId,
          substanceIds,
          labId,
          payloadSchemaId,
          labReceptionAddressId:
            labId === base.labId ? base.labReceptionAddressId : null
        }
      }
      return prev
    })
  }

  const step0Activate = true
  const step1Activate = canProceedFromStep0()
  const step2Activate = canProceedFromStep1() && hasMatrixAmount
  const step3Activate = step2Activate && canProceedFromStep2()

  const ValidAnalysisTypes = () => (
    <div>
      <Mtds.Button
        data-arrow
        data-variant='secondary'
        popoverTarget='create-task-type-popover'
      >
        Gyldige analysetyper{' '}
        <Mtds.Badge
          data-badge={validAnalysisTypes
            .map(({ analysisTypes = [] }) => analysisTypes.length)
            .reduce((a, b) => a + b, 0)}
        />
      </Mtds.Button>
      <Mtds.Popover id='create-task-type-popover' data-overscroll='contain'>
        {validAnalysisTypes?.map((contract, index) => {
          const contractTitle = contract.title
          const labName = batchData?.labs.find(
            (lab) => lab.id === contract.labId
          )?.name
          return (
            <menu key={contract.id}>
              <li>
                {index > 0 && <Mtds.Divider />}
                <Mtds.Heading as={'h4'} data-size={'xs'}>
                  {contractTitle} ({labName})
                </Mtds.Heading>
              </li>
              {(contract.analysisTypes ?? []).map(({ id, analysisType }) => (
                <li key={id}>
                  <Mtds.Button
                    popoverTargetAction='hide'
                    onClick={() => handleSelectAnalysisType(id, contract.id)}
                    className={'flex-row justify-between items-start'}
                  >
                    <Mtds.Grid>
                      <strong>
                        {analysisType?.sampleType?.matrix} (
                        {analysisType?.sampleType?.product})
                      </strong>
                      <Mtds.Muted>{`Skjema: ${analysisType?.payloadSchema?.name || '-'}`}</Mtds.Muted>
                    </Mtds.Grid>
                    <Mtds.Muted>{getSubstanceText(analysisType)}</Mtds.Muted>
                  </Mtds.Button>
                </li>
              ))}
            </menu>
          )
        })}
      </Mtds.Popover>
    </div>
  )
  return (
    <ModalDialog
      id={'copy-task-modal'}
      closedby='any'
      data-placement='right'
      open={isOpen}
      onClose={() => {
        setOpen(false)
      }}
    >
      <Mtds.Button
        command='close'
        commandfor={'copy-task-modal'}
        aria-label={'Lukk'}
      />
      <Mtds.Grid>
        <Mtds.Heading>Kopier oppgave</Mtds.Heading>
        {/* Step 0 */}
        {parentContractId ? (
          <Mtds.Info>
            <MicroscopeIcon />
            {parentContract?.title}
          </Mtds.Info>
        ) : (
          <Mtds.Details data-variant='card' open={step === 0}>
            <StepHeader
              index={0}
              title='Kontrakt'
              currentStep={step}
              canActivate={step0Activate}
              onToggleRequest={() => handleStepToggle(0)}
              isFinished={step1Activate}
            />
            <Mtds.Grid className={'p-1'} data-gap={6}>
              {isPendingBatchData && <Mtds.Skeleton />}
              {isErrorBatchData && <ErrorMessage error={batchDataError} />}
              {debug && <ValidAnalysisTypes />}
              {!isPendingBatchData && !isErrorBatchData && (
                <Form
                  noHtml5Validate
                  schema={step0Schema}
                  uiSchema={createTaskUiSchema}
                  widgets={widgets}
                  templates={templates}
                  showErrorList={false}
                  validator={validator}
                  formData={formData}
                  experimental_defaultFormStateBehavior={{
                    constAsDefaults: 'skipOneOf'
                  }}
                  onChange={(data) =>
                    setFormData({
                      ...formData,
                      ...data.formData,
                      labReceptionAddressId:
                        data.formData.labId === formData?.labId
                          ? formData?.labReceptionAddressId
                          : null
                    })
                  }
                  onSubmit={() => setStep(1)}
                >
                  <></>
                </Form>
              )}
            </Mtds.Grid>
          </Mtds.Details>
        )}
        {/* Step 1 */}
        {isOpen && formData?.labId != null && (
          <TaskReceptionAddress
            query={receptionAddress}
            value={formData.labReceptionAddressId}
            disabled={
              createTask.isPending ||
              existingOrCreateAnalysisType.isPending ||
              addContractAnalysisType.isPending
            }
            onChange={(labReceptionAddressId) =>
              setFormData((current) => ({ ...current, labReceptionAddressId }))
            }
          />
        )}
        <Mtds.Details data-variant='card' open={step === 1}>
          <StepHeader
            index={1}
            title='Prøvemateriale'
            currentStep={step}
            canActivate={step1Activate}
            onToggleRequest={() => handleStepToggle(1)}
            alertLabel={step === 1 ? undefined : step1AlertLabel}
            isFinished={step2Activate}
          />
          <Mtds.Grid className={'p-1'} data-gap={6}>
            {isPendingBatchData && <Mtds.Skeleton />}
            {isErrorBatchData && <ErrorMessage error={batchDataError} />}
            {!step1Activate && (
              <Mtds.Alert data-color={'warning'}>
                Fullfør forrige steg for å fortsette
              </Mtds.Alert>
            )}
            {debug && <ValidAnalysisTypes />}
            {!isPendingBatchData && !isErrorBatchData && step1Activate && (
              <Form
                noHtml5Validate
                schema={step1Schema}
                uiSchema={createTaskUiSchema}
                widgets={widgets}
                templates={templates}
                showErrorList={false}
                validator={validator}
                formData={formData}
                formContext={{
                  contractId: formData?.contractId
                }}
                experimental_defaultFormStateBehavior={{
                  constAsDefaults: 'skipOneOf'
                }}
                onChange={(data) =>
                  setFormData({ ...formData, ...data.formData })
                }
                onSubmit={() => setStep(2)}
              >
                <></>
              </Form>
            )}
            {!isPendingBatchData && !isErrorBatchData && step1Activate && (
              <MatrixAmountFields
                required
                draft={matrixAmountDraft}
                error={matrixAmountError}
                disabled={
                  createTask.isPending ||
                  existingOrCreateAnalysisType.isPending ||
                  addContractAnalysisType.isPending
                }
                onChange={setMatrixAmountDraft}
              />
            )}
            {!isPendingBatchData &&
              !isErrorBatchData &&
              showAwaitingApprovalWarning && (
                <Mtds.Alert data-color={'info'}>
                  <Mtds.Heading>Avventer godkjenning</Mtds.Heading>
                  Kombinasjonen avventer godkjenning hos laboratoriet.
                </Mtds.Alert>
              )}
            {!isPendingBatchData &&
              !isErrorBatchData &&
              showNewCombinationWarning && (
                <Mtds.Alert data-color={'info'}>
                  <Mtds.Heading>
                    Ny kombinasjon sendes til godkjenning
                  </Mtds.Heading>
                  Kombinasjonen sendes til godkjenning hos laboratoriet.
                </Mtds.Alert>
              )}
          </Mtds.Grid>
        </Mtds.Details>

        {/* Step 2 */}
        <Mtds.Details data-variant='card' open={step === 2}>
          <StepHeader
            index={2}
            title='Tilleggsinformasjon'
            currentStep={step}
            canActivate={step2Activate}
            onToggleRequest={() => handleStepToggle(2)}
            isFinished={step3Activate}
          />
          <Mtds.Grid className={'p-1'}>
            {isPendingBatchData && <Mtds.Skeleton />}
            {isErrorBatchData && <ErrorMessage error={batchDataError} />}
            {!step2Activate && (
              <Mtds.Alert data-color={'warning'}>
                Fullfør forrige steg for å fortsette
              </Mtds.Alert>
            )}
            {!isPendingBatchData && !isErrorBatchData && step2Activate && (
              <Form
                noHtml5Validate
                schema={step2Schema}
                uiSchema={createTaskUiSchema}
                widgets={widgets}
                templates={templates}
                showErrorList={false}
                validator={validator}
                formData={formData}
                experimental_defaultFormStateBehavior={{
                  constAsDefaults: 'skipOneOf'
                }}
                onChange={(data) =>
                  setFormData({ ...formData, ...data.formData })
                }
                onSubmit={() => setStep(3)}
              >
                <></>
              </Form>
            )}
          </Mtds.Grid>
        </Mtds.Details>

        {/* Step 3 */}
        <Mtds.Details data-variant='card' open={step === 3}>
          <StepHeader
            index={3}
            title='Antall prøver'
            currentStep={step}
            canActivate={step3Activate}
            onToggleRequest={() => handleStepToggle(3)}
            isFinished={canSubmit()}
          />
          <Mtds.Grid className={'p-1'}>
            {isPendingBatchData && <Mtds.Skeleton />}
            {isErrorBatchData && <ErrorMessage error={batchDataError} />}
            {!step3Activate && (
              <Mtds.Alert data-color={'warning'}>
                Fullfør forrige steg for å fortsette
              </Mtds.Alert>
            )}
            {!isPendingBatchData && !isErrorBatchData && step3Activate && (
              <Form
                noHtml5Validate
                schema={step3Schema}
                uiSchema={createTaskUiSchema}
                widgets={widgets}
                templates={templates}
                showErrorList={false}
                validator={validator}
                formData={formData}
                experimental_defaultFormStateBehavior={{
                  constAsDefaults: 'skipOneOf'
                }}
                onChange={(data) =>
                  setFormData({ ...formData, ...data.formData })
                }
                onSubmit={({ formData }) => {
                  if (canSubmit() && formData) {
                    submitFormData(formData as TaskFormData)
                  }
                }}
              >
                <Mtds.Flex className='mt-8'>
                  <Mtds.Button
                    data-variant='primary'
                    type='submit'
                    aria-busy={createTask.isPending}
                    disabled={
                      !canSubmit() ||
                      createTask.isPending ||
                      !destination.data?.canCreateChildren ||
                      destination.isFetching
                    }
                  >
                    Opprett
                  </Mtds.Button>
                </Mtds.Flex>
              </Form>
            )}
          </Mtds.Grid>
        </Mtds.Details>
      </Mtds.Grid>
    </ModalDialog>
  )
}
